import React, { useEffect, useState } from 'react';
import CAppPaper from '../../../../../../../app/components/CAppPaper';
import { useTheme, SxProps, Box } from '@mui/material';
import AppCustomIcon from '../../../../../../../app/features/icon/AppIcon';
import investmentKpiLang from '../../../../../langs/investmentKpiLang';
import AppFormatAmount from '../../../../../../../app/features/format/AppFormatAmount';
import { useTranslation } from 'react-i18next';
import investmentTypeLang from '../../../langs/investmentTypeLang';
import langInvestWordLang from '../../../../../langs/langInvestWordLang';
import IInvestKpis from '../../../../../interfaces/IInvestKpis';
import { TInvestFundCurrency } from '../../../../../types/fund/TInvestFundCurrency';

type ValueKpiProps = {
	kpis: IInvestKpis;
	currency: TInvestFundCurrency;
};

const CValueKpi = (props: ValueKpiProps) => {
	const { kpis, currency } = props;
	const { t } = useTranslation();
	const theme = useTheme();

	const [distributedInterestIncome, setDistributedInterestIncome] = useState<number>();
	const [distributedReturnOfCapital, setDistributedReturnOfCapital] = useState<number>();

	useEffect(() => {
		if (kpis) {
			setDistributedInterestIncome(kpis.distributedInterestIncome ?? undefined);
			setDistributedReturnOfCapital(kpis.distributedReturnOfCapital ?? undefined);
		}

		return () => {
			// Clean up the subscription
			setDistributedInterestIncome(undefined);
			setDistributedReturnOfCapital(undefined);
		};
	}, [kpis]);

	const styleTitle = {
		color: theme.palette.secondary.main,
		verticalAlign: 'text-bottom',
		boxSizing: 'inherit',
		textAlign: 'left',
		alignItems: 'center',
		display: 'inline-flex',
		fontWeight: '500',
		fontSize: '1.25rem',
		'& .icon': {
			marginRight: '10px',
		},
	};
	const styleKPI = {
		padding: '30px',
		backgroundColor: theme.palette.primary.main,
		border: '0px',
		borderRadius: '10px',
		textAlign: 'left',
		'& .title': styleTitle,
		'& .list': {
			color: 'white',
			textAlign: 'left',
			alignItems: 'center',
			padding: '10px 0px 0px 0px',
			fontWeight: 'normal',
			'& .item': {
				color: 'white',
				textAlign: 'left',
				alignItems: 'center',
				padding: '6px 0px',
				fontWeight: 'normal',
				'& .value': {
					textAlign: 'right',
					fontWeight: '600',
					fontSize: '1.25rem',
				},
				'& .label': {
					textAlign: 'left',
				},
			},
			'& .item:last-child': {
				paddingBottom: '0px',
			},
		},
	} as SxProps;

	const styleValue = {
		...styleKPI,
		backgroundColor: theme.palette.secondary.main,
		'& .title': {
			...styleTitle,
			color: 'white',
		},
	} as SxProps;

	return (
		<CAppPaper id="expertise-value-kpi-component" color="secondary" variant="outlined" sx={styleValue}>
			<Box className="title">
				<AppCustomIcon tag="kpi_value" className="icon" /> {t(investmentTypeLang.value.tag)}
			</Box>
			<Box className="list" style={{ padding: '20px 0px' }}>
				<Box
					style={{
						fontSize: '1rem',
						lineHeight: '1.5',
						fontWeight: '400',
						fontFamily: 'Poppins',
					}}
				>
					{t(investmentKpiLang.distributed_interest_income.tag)}
				</Box>
				<Box
					style={{
						fontSize: '1.875rem',
						lineHeight: '1.5',
						fontWeight: '700',
						fontFamily: 'Poppins',
						marginBottom: '35px',
					}}
				>
					<AppFormatAmount
						value={distributedInterestIncome}
						addSpace
						textIfNull={t(langInvestWordLang.nc.tag)}
						currency={currency}
					/>
				</Box>

				<Box
					style={{
						fontSize: '1rem',
						lineHeight: '1.5',
						fontWeight: '400',
						fontFamily: 'Poppins',
					}}
				>
					{t(investmentKpiLang.distributed_return_of_capital.tag)}
				</Box>
				<Box
					style={{
						fontSize: '1.875rem',
						lineHeight: '1.5',
						fontWeight: '700',
						fontFamily: 'Poppins',
					}}
				>
					<AppFormatAmount
						value={distributedReturnOfCapital}
						addSpace
						textIfNull={t(langInvestWordLang.nc.tag)}
						currency={currency}
					/>
				</Box>
			</Box>
		</CAppPaper>
	);
};

export default CValueKpi;
